import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { FileText, Printer, ShieldCheck, AlertTriangle, ClipboardCheck } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";

const TABS = [
  { key: "compliance", label: "Compliance report", icon: ShieldCheck },
  { key: "audit", label: "Audit summary", icon: ClipboardCheck },
  { key: "risk", label: "Risk report", icon: AlertTriangle },
];

const sevMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };
const priMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };

export default function ReportsPage() {
  const [tab, setTab] = useState("compliance");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (data[tab]) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        tab === "compliance" ? "/reports/compliance" :
        tab === "audit" ? "/reports/audit-summary" : "/reports/risk"
      );
      setData((prev) => ({ ...prev, [tab]: d }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const current = data[tab];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Executive summaries across compliance, audits, and risk. Click Print / PDF to export.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="report-print">
          <Printer className="w-4 h-4" /> Print / PDF
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border print:hidden">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`report-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading || !current ? (
        <div className="h-40 bg-muted animate-pulse rounded" />
      ) : (
        <>
          {/* Report header (print-friendly) */}
          <Card className="print:shadow-none">
            <CardContent className="p-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold">CCAMS — {TABS.find((t) => t.key === tab).label}</div>
                <div className="text-xs text-muted-foreground">
                  Generated {format(new Date(current.generated_at), "MMM d, yyyy HH:mm")} by {current.generated_by}
                </div>
              </div>
            </CardContent>
          </Card>

          {tab === "compliance" && <ComplianceReport data={current} />}
          {tab === "audit" && <AuditSummaryReport data={current} />}
          {tab === "risk" && <RiskReport data={current} />}
        </>
      )}
    </div>
  );
}

function ComplianceReport({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Executive summary</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Frameworks" value={data.frameworks.length} />
          <Stat label="Readiness score" value={`${data.readiness.score}%`} />
          <Stat label="Readiness grade" value={data.readiness.grade} />
          <Stat label="Total controls" value={data.frameworks.reduce((s, f) => s + f.total_controls, 0)} />
        </CardContent>
      </Card>

      {data.frameworks.map((f) => (
        <Card key={f.id} className="print:break-inside-avoid" data-testid={`compliance-fw-${f.code}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{f.name} <span className="text-xs text-muted-foreground">v{f.version || "—"}</span></CardTitle>
                <CardDescription>{f.implemented} of {f.total_controls} controls implemented ({f.completion}%)</CardDescription>
              </div>
              <div className="text-2xl font-semibold tabular-nums text-primary">{f.completion}%</div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-t border-b border-border">
                    <th className="text-left py-2 px-4 label-eyebrow">Control</th>
                    <th className="text-left py-2 px-4 label-eyebrow">Priority</th>
                    <th className="text-left py-2 px-4 label-eyebrow">Status</th>
                    <th className="text-left py-2 px-4 label-eyebrow">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {f.controls.map((c) => (
                    <tr key={c.id} className="border-b border-border">
                      <td className="py-2 px-4">{c.title}</td>
                      <td className="py-2 px-4"><Badge variant={priMap[c.priority]}>{c.priority}</Badge></td>
                      <td className="py-2 px-4">
                        <Badge variant={c.status === "implemented" ? "success" : c.status === "in_progress" ? "info" : c.status === "needs_review" ? "warning" : "neutral"}>
                          {c.status?.replace("_", " ")}
                        </Badge>
                      </td>
                      <td className="py-2 px-4 tabular-nums">{c.evidence_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AuditSummaryReport({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Executive summary</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Audits" value={data.audits.length} />
          <Stat label="Planned" value={data.totals.planned} />
          <Stat label="In progress" value={data.totals.in_progress} />
          <Stat label="Completed" value={data.totals.completed} />
          <Stat label="Findings" value={`${data.findings_total} (${data.critical_high_findings} critical/high)`} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Audits</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2 px-4 label-eyebrow">Title</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Framework</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Status</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Progress</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Findings</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Due</th>
                </tr>
              </thead>
              <tbody>
                {data.audits.map((a) => (
                  <tr key={a.id} className="border-b border-border" data-testid={`report-audit-${a.id}`}>
                    <td className="py-2 px-4 font-medium">{a.title}</td>
                    <td className="py-2 px-4"><Badge variant="primary">{a.framework}</Badge></td>
                    <td className="py-2 px-4"><Badge variant="neutral">{a.status?.replace("_", " ")}</Badge></td>
                    <td className="py-2 px-4 tabular-nums">{a.progress}%</td>
                    <td className="py-2 px-4">{a.findings_count} ({a.critical_high_findings} C/H)</td>
                    <td className="py-2 px-4 text-muted-foreground">{a.due_date ? format(new Date(a.due_date), "MMM d, yyyy") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RiskReport({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Executive summary</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total risks" value={data.total} />
          <Stat label="Critical + High" value={(data.by_severity.critical || 0) + (data.by_severity.high || 0)} />
          <Stat label="Open" value={data.by_status.open || 0} />
          <Stat label="Accepted" value={data.by_status.accepted || 0} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Risk register</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2 px-4 label-eyebrow">Risk</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Category</th>
                  <th className="text-left py-2 px-4 label-eyebrow">L × I</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Inherent</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Residual</th>
                  <th className="text-left py-2 px-4 label-eyebrow">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.risks.map((r) => (
                  <tr key={r.id} className="border-b border-border" data-testid={`report-risk-${r.id}`}>
                    <td className="py-2 px-4 font-medium max-w-xs truncate">{r.title}</td>
                    <td className="py-2 px-4"><Badge variant="neutral">{r.category}</Badge></td>
                    <td className="py-2 px-4 tabular-nums">L{r.likelihood} × I{r.impact}</td>
                    <td className="py-2 px-4"><Badge variant={sevMap[r.severity]}>{r.score} · {r.severity}</Badge></td>
                    <td className="py-2 px-4 tabular-nums">{r.residual_score}</td>
                    <td className="py-2 px-4"><Badge variant="neutral">{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
